import {
  CLIPBOARD_IMAGE_MAX_BYTES,
  CLIPBOARD_TEXT_MAX_BYTES,
  MAX_FILE_BYTES,
  MAX_TRANSFER_FILES,
  TRANSFER_BUFFER_HIGH_BYTES,
  TRANSFER_CHUNK_BYTES,
  TRANSFER_EARLY_BYTES,
  clipboardTextFits,
  isSafeFileName,
  splitClipboardText,
  type HostInputMessage,
  type ViewerInputMessage,
} from '@viewer/protocol';
import { INSIDE_APP } from '../../lib/config';
import {
  clipboardOffer,
  clipboardPullEnd,
  clipboardPullFail,
  clipboardPullWrite,
  downloadAbort,
  downloadBegin,
  downloadWrite,
  localClipboardImage,
  localClipboardRead,
  localClipboardWriteFiles,
  localClipboardWriteImage,
  localClipboardWriteText,
  readFileChunk,
} from '../../lib/tauri/clipboard';
import { FileCutter, sendInChunks, type ChunkSend } from './transferBytes';

const READ_BYTES = 256 * 1024;
const IMAGE_NAME = 'imagem.png';

export interface TransferProgress {
  label: string;
  percent: number;
}

export interface RemoteFiles {
  count: number;
  skippedFolders: number;
}

export interface TransferView {
  remoteFiles: RemoteFiles | null;
  progress: TransferProgress | null;
}

interface OutgoingSource {
  name: string;
  size: number;
  read(offset: number, length: number): Promise<Uint8Array>;
}

interface IncomingFile {
  name: string;
  size: number;
  offset: number;
  id?: string;
  path?: string;
  parts?: Uint8Array[];
}

interface Download {
  id: string;
  cutter: FileCutter;
  files: IncomingFile[];
  total: number;
  got: number;
}

interface Deps {
  send(message: ViewerInputMessage): boolean;
  channel(): RTCDataChannel | null;
  setView(view: TransferView): void;
  notify(kind: 'error' | 'info', text: string): void;
  /** A área de transferência local estava vazia: manda o Ctrl+V como tecla. */
  forwardPasteKey(): void;
  clipboard: boolean;
  fileTransfer: boolean;
}

export class SessionTransfer {
  private remoteFiles: RemoteFiles | null = null;
  private progress: TransferProgress | null = null;
  private lastPaint = 0;

  private uploading = false;
  private uploadId: string | null = null;
  private uploadAbort: AbortController | null = null;

  private waitingDownload: string | null = null;
  private announced: { clipboardId: string; files: { name: string; size: number }[] } | null = null;
  private sink: 'save' | 'virtual' | 'image' = 'save';
  /** Imagem anunciada pelo Host. `requested`: já foi pedida, não pede de novo. */
  private remoteImage: { clipboardId: string; requested: boolean } | null = null;
  /** De qual aviso é a imagem que está chegando. */
  private imageFor: string | null = null;
  /** Sequência da área de transferência local logo depois de gravar a imagem do Host. */
  private wroteImage: number | null = null;
  /** Próximo índice da lista, quando o botão do navegador baixa um arquivo por vez. */
  private saveNext: number | null = null;
  /** O Explorer pode abrir outro arquivo virtual antes do atual terminar. */
  private waitingPulls: { clipboardId: string; index: number; transferId: string }[] = [];
  private incoming: Download | null = null;
  private queue: Uint8Array[] = [];
  private pumping = false;

  /** Partes de `clipboard_text` do Host até chegar a última. `null`: passou do limite. */
  private remoteText: string | null = '';

  private earlyParts: Uint8Array[] = [];
  private earlyLength = 0;
  private ignoreUntil = 0;
  private ignoreHard = 0;

  private pendingCopy: string | null = null;
  private listening = false;
  private readonly onGesture = () => {
    const text = this.pendingCopy;
    if (text === null) return;
    this.pendingCopy = null;
    copyWithExec(text);
    this.disarm();
  };

  constructor(private readonly deps: Deps) {}

  onHostMessage(message: HostInputMessage): boolean {
    switch (message.type) {
      case 'clipboard_text': {
        if (!this.deps.clipboard) return true;
        this.remoteImage = null;
        const text = this.remoteText === null ? null : this.remoteText + message.text;
        // Comprimento em UTF-16 nunca passa dos bytes em UTF-8: o corte aqui é seguro.
        this.remoteText = text !== null && text.length <= CLIPBOARD_TEXT_MAX_BYTES ? text : null;
        if (message.more) return true;
        const complete = this.remoteText;
        this.remoteText = '';
        if (complete === null || !clipboardTextFits(complete)) {
          this.deps.notify('error', 'O texto copiado no computador remoto é grande demais.');
          return true;
        }
        this.remoteFiles = null;
        this.publish();
        void this.applyRemoteText(complete);
        return true;
      }
      case 'clipboard_files':
        this.remoteImage = null;
        if (!this.deps.clipboard || !this.deps.fileTransfer) return true;
        this.announced = { clipboardId: message.clipboardId, files: message.files };
        this.remoteFiles = { count: message.files.length, skippedFolders: message.skippedFolders };
        this.publish();
        if (INSIDE_APP && message.files.length > 0) {
          void clipboardOffer(message.clipboardId, message.files).catch((error: unknown) => {
            this.deps.notify('error', messageOf(error));
          });
        }
        return true;
      case 'clipboard_image':
        if (!this.deps.clipboard || !canWriteImage()) return true;
        this.remoteImage = { clipboardId: message.clipboardId, requested: false };
        this.announced = null;
        this.remoteFiles = null;
        this.publish();
        this.fetchRemoteImage();
        return true;
      case 'files_offer':
        if (message.transferId !== this.waitingDownload) return true;
        this.waitingDownload = null;
        if (this.sink === 'image' && (message.files.length !== 1 || (message.files[0]?.size ?? 0) > CLIPBOARD_IMAGE_MAX_BYTES)) {
          this.deps.notify('error', 'A imagem copiada no computador remoto é grande demais.');
          this.abortDownload(message.transferId);
          return true;
        }
        this.startDownload(message.transferId, message.files);
        return true;
      case 'transfer_done':
        return true;
      case 'transfer_error':
        this.deps.notify('error', message.message);
        if (this.incoming?.id === message.transferId || this.waitingDownload === message.transferId) {
          void this.failDownload();
        }
        return true;
      default:
        return false;
    }
  }

  onBytes(chunk: Uint8Array) {
    if (this.dropping()) return;
    if (!this.incoming) {
      this.pushEarly(chunk);
      return;
    }
    this.queue.push(chunk);
    void this.pump();
  }

  pasteLocal() {
    void this.pasteFromApp();
  }

  /** `true` quando o evento `paste` tinha texto ou arquivo. Vazio: o atalho segue como tecla. */
  pasteData(data: DataTransfer): boolean {
    if (!this.deps.clipboard) return false;
    const picked = filesFromDataTransfer(data);
    if (picked.files.length > 0 && !this.deps.fileTransfer) return false;
    if (picked.skippedFolders && picked.files.length === 0) {
      this.deps.notify('info', 'Pastas ainda não são suportadas.');
      return true;
    }
    if (picked.files.length > 0) {
      if (picked.skippedFolders) this.deps.notify('info', 'Pastas ainda não são suportadas.');
      void this.offer(picked.files.map(sourceFromFile), true);
      return true;
    }
    const text = data.getData('text/plain');
    if (!text) return false;
    this.sendText(text, true);
    return true;
  }

  sendFiles(files: File[]) {
    if (!this.deps.fileTransfer) return;
    void this.offer(files.map(sourceFromFile), false);
  }

  sendDropped(data: DataTransfer) {
    if (!this.deps.fileTransfer) return;
    const picked = filesFromDataTransfer(data);
    if (picked.skippedFolders) this.deps.notify('info', 'Pastas ainda não são suportadas.');
    if (picked.files.length === 0) return;
    void this.offer(picked.files.map(sourceFromFile), false);
  }

  downloadCopied() {
    if (!this.deps.clipboard || !this.deps.fileTransfer) return;
    if (!this.announced?.files.length) return;
    if (this.incoming || this.waitingDownload) {
      this.deps.notify('error', 'Já existe uma transferência em andamento.');
      return;
    }
    if (!this.deps.channel()) {
      this.deps.notify('error', 'O canal de transferência ainda não está aberto.');
      return;
    }
    this.saveNext = 0;
    this.requestFile(this.announced.clipboardId, 0, newId(), 'save');
  }

  /** O Explorer começou a colar um arquivo virtual. O id é o do pedido no Rust. */
  pullRemote(clipboardId: string, index: number, transferId: string) {
    if (!this.deps.clipboard || !this.deps.fileTransfer || this.announced?.clipboardId !== clipboardId) {
      void clipboardPullFail(transferId);
      return;
    }
    if (this.incoming || this.waitingDownload) {
      if (this.sink !== 'save') {
        this.waitingPulls.push({ clipboardId, index, transferId });
        return;
      }
      void clipboardPullFail(transferId);
      this.deps.notify('error', 'Já existe uma transferência em andamento.');
      return;
    }
    if (!this.deps.channel()) {
      void clipboardPullFail(transferId);
      this.deps.notify('error', 'O canal de transferência ainda não está aberto.');
      return;
    }
    this.saveNext = null;
    this.requestFile(clipboardId, index, transferId, 'virtual');
  }

  /** O download terminou: primeiro o Explorer que está esperando, depois a imagem anunciada. */
  private nextDownload() {
    const pull = this.waitingPulls.shift();
    if (pull) this.pullRemote(pull.clipboardId, pull.index, pull.transferId);
    this.fetchRemoteImage();
  }

  /** O canal "transfer" abriu: a imagem anunciada na abertura da sessão já pode vir. */
  transferOpened() {
    this.fetchRemoteImage();
  }

  /** A imagem do Host vem assim que o canal estiver livre, para o Ctrl+V local já encontrá-la. */
  private fetchRemoteImage() {
    const image = this.remoteImage;
    if (!image || image.requested || this.incoming || this.waitingDownload || !this.deps.channel()) return;
    // Bytes de uma transferência cancelada ainda chegando seriam lidos como os da imagem.
    const settle = this.ignoreHard ? this.ignoreUntil - performance.now() : 0;
    if (settle > 0) {
      setTimeout(() => this.fetchRemoteImage(), settle);
      return;
    }
    image.requested = true;
    const transferId = newId();
    this.sink = 'image';
    this.saveNext = null;
    this.imageFor = image.clipboardId;
    this.waitingDownload = transferId;
    if (!this.deps.send({ type: 'image_request', clipboardId: image.clipboardId, transferId })) {
      this.waitingDownload = null;
    }
  }

  /** O Explorer soltou o arquivo no meio da cópia. */
  cancelPull(transferId: string) {
    const queued = this.waitingPulls.findIndex((pull) => pull.transferId === transferId);
    if (queued >= 0) {
      this.waitingPulls.splice(queued, 1);
      return;
    }
    if (this.waitingDownload !== transferId && this.incoming?.id !== transferId) return;
    this.deps.send({ type: 'transfer_error', transferId, message: 'Transferência cancelada.' });
    void this.failDownload();
  }

  private requestFile(clipboardId: string, index: number, transferId: string, sink: 'save' | 'virtual') {
    this.sink = sink;
    this.waitingDownload = transferId;
    if (!this.deps.send({ type: 'file_request', clipboardId, index, transferId })) {
      this.waitingDownload = null;
      if (sink === 'virtual') void clipboardPullFail(transferId);
      this.deps.notify('error', 'Não foi possível baixar os arquivos.');
    }
  }

  cancel() {
    if (this.uploadAbort && this.uploadId) {
      this.deps.send({
        type: 'transfer_error',
        transferId: this.uploadId,
        message: 'Transferência cancelada.',
      });
      this.uploadAbort.abort();
      return;
    }
    const id = this.incoming?.id ?? this.waitingDownload;
    if (id) this.abortDownload(id);
  }

  /** Avisa o Host e descarta os bytes que ainda estiverem a caminho. */
  private abortDownload(transferId: string) {
    this.deps.send({ type: 'transfer_error', transferId, message: 'Transferência cancelada.' });
    const now = performance.now();
    this.ignoreUntil = now + 400;
    this.ignoreHard = now + 5000;
    void this.failDownload();
  }

  close() {
    this.disarm();
    this.remoteImage = null;
    this.uploadAbort?.abort();
    for (const pull of this.waitingPulls) void clipboardPullFail(pull.transferId);
    this.waitingPulls = [];
    void this.failDownload();
  }

  private async pasteFromApp() {
    if (!this.deps.clipboard) {
      this.deps.forwardPasteKey();
      return;
    }
    try {
      const clip = await localClipboardRead();
      if (clip.files.length > 0) {
        if (!this.deps.fileTransfer) {
          this.deps.forwardPasteKey();
          return;
        }
        if (clip.skippedFolders > 0) this.deps.notify('info', 'Pastas ainda não são suportadas.');
        await this.offer(
          clip.files.map((file) => ({
            name: file.name,
            size: file.size,
            read: (offset, length) => readFileChunk(file.path, offset, length),
          })),
          true,
        );
        return;
      }
      if (clip.skippedFolders > 0) {
        this.deps.notify('info', 'Pastas ainda não são suportadas.');
        return;
      }
      if (clip.text) {
        this.sendText(clip.text, true);
        return;
      }
      if (clip.image) {
        // É a imagem que veio do Host: ele ainda a tem, basta colar.
        if (clip.sequence === this.wroteImage) {
          this.deps.forwardPasteKey();
          return;
        }
        const png = await localClipboardImage();
        await this.upload([{ name: IMAGE_NAME, size: png.byteLength, read: async (offset, length) => png.subarray(offset, offset + length) }], (transferId) => ({
          type: 'clipboard_image',
          transferId,
          size: png.byteLength,
        }));
        return;
      }
      this.deps.forwardPasteKey();
    } catch (error) {
      this.deps.notify('error', messageOf(error));
    }
  }

  private async offer(sources: OutgoingSource[], paste: boolean) {
    const problem = validateFiles(sources);
    if (problem) {
      this.deps.notify('error', problem);
      return;
    }
    const files = sources.map((file) => ({ name: file.name, size: file.size }));
    await this.upload(sources, (transferId) => ({ type: 'files_offer', transferId, files, paste }));
  }

  /** `header` é a mensagem que anuncia os bytes no canal "input". */
  private async upload(sources: OutgoingSource[], header: (transferId: string) => ViewerInputMessage) {
    if (this.uploading) {
      this.deps.notify('error', 'Já existe uma transferência em andamento.');
      return;
    }
    const channel = this.deps.channel();
    if (!channel) {
      this.deps.notify('error', 'O canal de transferência ainda não está aberto.');
      return;
    }
    const id = newId();
    if (!this.deps.send(header(id))) {
      this.deps.notify('error', 'Não foi possível enviar os arquivos.');
      return;
    }
    this.uploading = true;
    this.uploadId = id;
    const abort = new AbortController();
    this.uploadAbort = abort;
    const total = sources.reduce((sum, file) => sum + file.size, 0);
    let sent = 0;
    try {
      for (const file of sources) {
        let offset = 0;
        this.paint(file.name, sent, total);
        while (offset < file.size) {
          if (abort.signal.aborted) throw new TransferStopped();
          const length = Math.min(READ_BYTES, file.size - offset);
          const chunk = await file.read(offset, length);
          if (chunk.byteLength === 0) throw new Error('Não foi possível ler os arquivos.');
          const wrote = await sendInChunks(chunk, chunkSend(channel, abort.signal));
          offset += wrote;
          sent += wrote;
          if (abort.signal.aborted) throw new TransferStopped();
          if (wrote < chunk.byteLength) throw new Error('A transferência foi interrompida.');
          this.paint(file.name, sent, total);
        }
      }
    } catch (error) {
      if (!(error instanceof TransferStopped)) this.deps.notify('error', messageOf(error));
      this.paint('Cancelando…', sent, total);
      try {
        await padZeros(channel, total - sent);
      } catch (padError) {
        console.warn('não foi possível encerrar a transferência', padError);
      }
    } finally {
      this.uploading = false;
      this.uploadId = null;
      this.uploadAbort = null;
      if (!this.incoming) {
        this.progress = null;
        this.publish();
      }
    }
  }

  private sendText(text: string, paste: boolean) {
    if (!clipboardTextFits(text)) {
      this.deps.notify('error', 'O texto da área de transferência é grande demais.');
      return;
    }
    const parts = splitClipboardText(text);
    const sent = parts.every((part, index) =>
      this.deps.send({ type: 'clipboard_text', text: part, paste, more: index < parts.length - 1 }),
    );
    if (!sent) {
      this.deps.notify('error', 'Não foi possível enviar o texto da área de transferência.');
    }
  }

  private async applyRemoteText(text: string) {
    if (INSIDE_APP) {
      try {
        await localClipboardWriteText(text);
      } catch (error) {
        this.deps.notify('error', messageOf(error));
      }
      return;
    }
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (error) {
        console.warn('não foi possível gravar a área de transferência', error);
      }
    }
    this.pendingCopy = text;
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('pointerdown', this.onGesture, true);
    window.addEventListener('keydown', this.onGesture, true);
  }

  private startDownload(id: string, files: { name: string; size: number }[]) {
    const early = this.takeEarly();
    this.incoming = {
      id,
      cutter: new FileCutter(files.map((file) => file.size)),
      files: files.map((file) => ({ name: file.name, size: file.size, offset: 0 })),
      total: files.reduce((sum, file) => sum + file.size, 0),
      got: 0,
    };
    if (early.byteLength) this.queue.unshift(early);
    void this.pump();
  }

  private async pump() {
    if (this.pumping) return;
    this.pumping = true;
    try {
      while (this.queue.length && this.incoming) {
        const chunk = this.queue.shift();
        if (chunk) await this.consume(chunk);
      }
    } catch (error) {
      this.deps.notify('error', messageOf(error));
      await this.failDownload();
    } finally {
      this.pumping = false;
      if (this.queue.length && this.incoming) void this.pump();
    }
  }

  private async consume(chunk: Uint8Array) {
    const download = this.incoming;
    if (!download) {
      this.pushEarly(chunk);
      return;
    }
    const { slices, done, extra } = download.cutter.push(chunk);
    for (const slice of slices) await this.takeSlice(download, slice);
    if (!done) return;
    await this.finishDownload();
    if (extra.byteLength) this.pushEarly(extra);
  }

  private async takeSlice(download: Download, slice: { index: number; bytes: Uint8Array }) {
    const file = download.files[slice.index];
    if (!file) return;
    if (this.sink === 'virtual') {
      if (slice.bytes.byteLength > 0) await writePull(download.id, file.offset, slice.bytes);
    } else if (this.sink === 'save' && INSIDE_APP) {
      if (!file.id) {
        const started = await downloadBegin(file.name);
        file.id = started.id;
        file.path = started.path;
      }
      if (slice.bytes.byteLength > 0 && file.id) await downloadWrite(file.id, file.offset, slice.bytes.slice());
    } else if (slice.bytes.byteLength > 0) {
      file.parts ??= [];
      file.parts.push(slice.bytes.slice());
    }
    file.offset += slice.bytes.byteLength;
    download.got += slice.bytes.byteLength;
    this.paint(file.name, download.got, download.total);
    if (this.sink === 'save' && !INSIDE_APP && file.offset === file.size) {
      downloadBlob(file.name, file.parts ?? []);
      file.parts = [];
    }
  }

  private async finishDownload() {
    const download = this.incoming;
    if (!download?.cutter.done) return;
    const sink = this.sink;
    const next = this.saveNext;
    this.incoming = null;
    this.progress = null;
    this.publish();
    if (sink === 'image') {
      await this.applyRemoteImage(joinBytes(download.files[0]?.parts ?? []));
      this.nextDownload();
      return;
    }
    if (sink === 'virtual') {
      await clipboardPullEnd(download.id);
      this.nextDownload();
      return;
    }
    if (INSIDE_APP) {
      const paths = download.files.flatMap((file) => (file.path ? [file.path] : []));
      if (paths.length) {
        await localClipboardWriteFiles(paths);
        this.deps.notify('info', 'Arquivos salvos em Downloads e prontos para colar.');
      }
    }
    const announced = this.announced;
    if (next !== null && announced && next + 1 < announced.files.length) {
      this.saveNext = next + 1;
      this.requestFile(announced.clipboardId, next + 1, newId(), 'save');
      return;
    }
    this.saveNext = null;
    this.nextDownload();
  }

  /** Só grava se, durante o download, o Host não tiver copiado outra coisa. */
  private async applyRemoteImage(png: Uint8Array) {
    if (this.remoteImage?.clipboardId !== this.imageFor) return;
    if (INSIDE_APP) {
      try {
        this.wroteImage = await localClipboardWriteImage(png);
      } catch (error) {
        this.deps.notify('error', messageOf(error));
      }
      return;
    }
    try {
      const blob = new Blob([toArrayBuffer(png)], { type: 'image/png' });
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    } catch (error) {
      console.warn('não foi possível gravar a imagem na área de transferência', error);
    }
  }

  private async failDownload() {
    const download = this.incoming;
    const waiting = this.waitingDownload;
    const sink = this.sink;
    this.incoming = null;
    this.waitingDownload = null;
    this.saveNext = null;
    this.queue = [];
    this.progress = null;
    this.publish();
    if (sink === 'virtual') {
      const id = download?.id ?? waiting;
      if (id) await clipboardPullFail(id).catch(() => undefined);
    } else if (sink === 'save' && download && INSIDE_APP) {
      await Promise.all(download.files.map((file) => (file.id ? downloadAbort(file.id).catch(() => undefined) : undefined)));
    }
    this.nextDownload();
  }

  private pushEarly(chunk: Uint8Array) {
    if (!chunk.byteLength) return;
    if (this.earlyLength + chunk.byteLength > TRANSFER_EARLY_BYTES) {
      this.earlyParts = [];
      this.earlyLength = 0;
      this.deps.notify('error', 'A transferência foi interrompida.');
      return;
    }
    this.earlyParts.push(chunk.slice());
    this.earlyLength += chunk.byteLength;
  }

  private takeEarly(): Uint8Array {
    const parts = this.earlyParts;
    this.earlyParts = [];
    this.earlyLength = 0;
    return joinBytes(parts);
  }

  private dropping(): boolean {
    if (!this.ignoreHard) return false;
    const now = performance.now();
    if (now >= this.ignoreHard || now >= this.ignoreUntil) {
      this.ignoreHard = 0;
      this.ignoreUntil = 0;
      return false;
    }
    this.ignoreUntil = Math.min(this.ignoreHard, now + 400);
    return true;
  }

  private paint(label: string, sent: number, total: number) {
    const percent = total <= 0 ? 100 : Math.min(100, Math.round((sent / total) * 100));
    const now = performance.now();
    if (percent !== 100 && this.progress?.label === label && now - this.lastPaint < 200) return;
    this.lastPaint = now;
    this.progress = { label, percent };
    this.publish();
  }

  private publish() {
    this.deps.setView({ remoteFiles: this.remoteFiles, progress: this.progress });
  }

  private disarm() {
    this.pendingCopy = null;
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('pointerdown', this.onGesture, true);
    window.removeEventListener('keydown', this.onGesture, true);
  }
}

function validateFiles(files: { name: string; size: number }[]): string | null {
  if (files.length === 0 || files.length > MAX_TRANSFER_FILES) {
    return 'A lista de arquivos da transferência é inválida.';
  }
  for (const file of files) {
    if (!isSafeFileName(file.name)) return 'O nome de um dos arquivos não é válido.';
    if (!Number.isInteger(file.size) || file.size < 0 || file.size > MAX_FILE_BYTES) {
      return 'Um dos arquivos é grande demais.';
    }
  }
  return null;
}

function sourceFromFile(file: File): OutgoingSource {
  return {
    name: file.name,
    size: file.size,
    read: async (offset, length) => new Uint8Array(await file.slice(offset, offset + length).arrayBuffer()),
  };
}

function filesFromDataTransfer(data: DataTransfer): { files: File[]; skippedFolders: boolean } {
  const files: File[] = [];
  let skippedFolders = false;
  for (const item of data.items) {
    if (item.kind !== 'file') continue;
    if (item.webkitGetAsEntry?.()?.isDirectory) {
      skippedFolders = true;
      continue;
    }
    const file = item.getAsFile();
    if (file) files.push(file);
  }
  if (files.length === 0 && !skippedFolders) return { files: [...data.files], skippedFolders: false };
  return { files, skippedFolders };
}

function chunkSend(channel: RTCDataChannel, signal: AbortSignal): ChunkSend {
  return {
    chunkSize: TRANSFER_CHUNK_BYTES,
    highWater: TRANSFER_BUFFER_HIGH_BYTES,
    bufferedAmount: () => channel.bufferedAmount,
    waitUntilLow: () => waitLow(channel, signal),
    send: (chunk) => channel.send(toArrayBuffer(chunk)),
    isAborted: () => signal.aborted,
  };
}

function waitLow(channel: RTCDataChannel, signal: AbortSignal): Promise<void> {
  if (channel.bufferedAmount <= TRANSFER_BUFFER_HIGH_BYTES) return Promise.resolve();
  if (signal.aborted) return Promise.reject(new TransferStopped());
  return new Promise((resolve, reject) => {
    const finish = (fn: () => void) => {
      channel.removeEventListener('bufferedamountlow', onLow);
      signal.removeEventListener('abort', onAbort);
      fn();
    };
    const onLow = () => finish(resolve);
    const onAbort = () => finish(() => reject(new TransferStopped()));
    channel.addEventListener('bufferedamountlow', onLow);
    signal.addEventListener('abort', onAbort);
  });
}

async function padZeros(channel: RTCDataChannel, count: number) {
  const zeros = new Uint8Array(TRANSFER_CHUNK_BYTES);
  let left = count;
  while (left > 0) {
    if (channel.bufferedAmount > TRANSFER_BUFFER_HIGH_BYTES) {
      await waitLow(channel, new AbortController().signal);
    }
    const n = Math.min(zeros.length, left);
    channel.send(toArrayBuffer(zeros.subarray(0, n)));
    left -= n;
  }
}

class TransferStopped extends Error {
  constructor() {
    super('Transferência cancelada.');
    this.name = 'TransferStopped';
  }
}

function newId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function messageOf(error: unknown): string {
  if (typeof error === 'string' && error) return error;
  if (error instanceof Error && error.message) return error.message;
  return 'A transferência foi interrompida.';
}

function copyWithExec(text: string) {
  const onCopy = (event: ClipboardEvent) => {
    event.clipboardData?.setData('text/plain', text);
    event.preventDefault();
  };
  document.addEventListener('copy', onCopy);
  try {
    document.execCommand('copy');
  } finally {
    document.removeEventListener('copy', onCopy);
  }
}

async function writePull(transferId: string, offset: number, bytes: Uint8Array) {
  const max = 1024 * 1024;
  let at = 0;
  while (at < bytes.byteLength) {
    const end = Math.min(at + max, bytes.byteLength);
    await clipboardPullWrite(transferId, offset + at, bytes.subarray(at, end));
    at = end;
  }
}

function joinBytes(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** No navegador, só com a API assíncrona da área de transferência. */
function canWriteImage(): boolean {
  return INSIDE_APP || (window.isSecureContext && typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function downloadBlob(name: string, parts: Uint8Array[]) {
  const url = URL.createObjectURL(new Blob(parts.map((part) => toArrayBuffer(part))));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
